import { isEmpty } from 'ramda';
import React, { forwardRef, useEffect, useState } from 'react';
import styled, { withTheme } from 'styled-components';

import { Icon } from '@ui/icons/Icon.tsx';
import Tooltip from '@ui/tooltip';
import { rgba } from '@utils/dom.ts';

import { handleKeyboardSelect } from '../../../plugins/utils/keyboardUtils.ts';
import { useCSSColors } from '../../hooks/useCSSColors.ts';

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  input:placeholder-shown + div {
    display: none;
  }
`;

const StyledInput = styled.input<{ $color?: string; $isValid?: boolean }>`
  background: transparent;
  border-radius: 8px;
  border: 0;
  color: ${props => (props.$isValid ? props.$color : '#D83131')};
  min-width: 1px;
  overflow: hidden;
  padding-block: 8px;
  padding-inline: 4px 8px;
  text-overflow: ${props => (isEmpty(String(props.value ?? '')) ? 'clip' : 'ellipsis')};
  white-space: ${props => (isEmpty(String(props.value ?? '')) ? 'normal' : 'nowrap')};
  width: 100%;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${props => rgba(props.$color ?? '#333333', 0.8)};
  }

  &::-ms-clear {
    display: none;
  }
`;

const ClearButton = styled.button`
  cursor: pointer;
  position: relative;
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover > .tooltip {
    display: block;
  }
  &:focus-visible {
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

type Input2Props = Omit<React.ComponentPropsWithoutRef<'input'>, 'color' | 'onChange' | 'value'> & {
  T: (key: string) => string;
  color?: string;
  handleClearButtonKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  hasClearButton?: boolean;
  isValid?: boolean;
  onChange: (value: string) => void;
  tooltipArrowPosition?: 'bottom' | 'left' | 'right' | 'top' | 'topright' | 'bottom-topright';
  value: string;
};

const Input2Component = forwardRef<HTMLInputElement, Input2Props>((props, ref) => {
  const colors = useCSSColors();
  const {
    hasClearButton = true,
    value,
    onChange,
    onKeyPress,
    color,
    isValid = true,
    className,
    T,
    handleClearButtonKeyDown,
    tooltipArrowPosition = 'bottom',
    ...otherProps
  } = props;

  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const valueChanged = (val: string) => {
    setLocalValue(val);
    onChange(val);
    document.getElementById('tooltip')?.setAttribute('style', 'display: none;');
  };

  // Ensure colors.widgetIconFill has a fallback for Safari compatibility
  const iconFillColor = colors?.widgetIconFill || '#333333';

  return (
    <InputWrapper className={className}>
      <StyledInput
        type="text"
        ref={ref}
        $isValid={isValid}
        $color={color}
        value={localValue}
        onKeyPress={onKeyPress}
        onChange={e => valueChanged(e.target.value)}
        {...otherProps}
      />
      {hasClearButton && value.length ? (
        <Tooltip text={T('ui:Clear Search')} arrowPosition={tooltipArrowPosition}>
          <ClearButton
            onMouseDown={e => {
              e.preventDefault();
              valueChanged('');
            }}
            data-testid={'searchClearButton'}
            tabIndex={0}
            aria-label={T('ui:Clear Search')}
            type="button"
            onKeyDown={handleClearButtonKeyDown || handleKeyboardSelect(() => valueChanged(''))}
          >
            <Icon name="close" cdn width={32} height={32} fillColor={iconFillColor} />
          </ClearButton>
        </Tooltip>
      ) : null}
    </InputWrapper>
  );
});

Input2Component.displayName = 'Input2';

export const Input2 = withTheme(Input2Component);
